import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Briefcase } from 'lucide-react';
import { useMattersWithClients } from '../hooks/useMatters';
import { usePageNavigation } from '../contexts/NavigationContext';
import EnhancedMatterList from '../components/matters/EnhancedMatterList';
import MatterForm from '../components/matters/MatterForm';
import type { MatterWithClient, MatterFormData, UpdateMatterRequest } from '../types';

const Matters: React.FC = () => {
  const navigate = useNavigate();
  // Use enhanced hook to get matters with client information
  const { matters, isLoading, error } = useMattersWithClients();
  const { setPageNavigation } = usePageNavigation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');

  // Set up page navigation
  React.useEffect(() => {
    setPageNavigation('Matters', [
      { label: 'Dashboard', href: '/' },
      { label: 'Matters', href: '/matters' }
    ]);
  }, [setPageNavigation]);

  // Get unique clients for filtering
  const uniqueClients = Array.from(new Set(matters.map(matter => matter.client.id)))
    .map(clientId => {
      const matter = matters.find(m => m.client.id === clientId);
      return matter ? { id: clientId, name: matter.client.name } : null;
    })
    .filter(Boolean) as { id: string; name: string }[];

  // Filter matters based on search, status, and client
  const filteredMatters = matters.filter(matter => {
    const matchesSearch = matter.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         matter.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         matter.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (matter.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false);
    
    const matchesStatus = statusFilter === 'all' || matter.status === statusFilter;
    const matchesClient = clientFilter === 'all' || matter.client.id === clientFilter;
    
    return matchesSearch && matchesStatus && matchesClient;
  });

  const handleMatterClick = (matter: MatterWithClient) => {
    navigate(`/matters/${matter.id}`);
  };

  const handleEditMatter = (matter: MatterWithClient) => {
    // TODO: Implement edit functionality - could open a modal or navigate to edit page
    console.log('Edit matter:', matter);
  };

  const handleDeleteMatter = async (matter: MatterWithClient) => {
    if (window.confirm(`Are you sure you want to delete "${matter.title}"?`)) {
      // TODO: Implement delete functionality with enhanced hook
      console.log('Delete matter:', matter);
    }
  };

  const handleClientClick = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  const handleCreateMatter = async (data: MatterFormData | UpdateMatterRequest) => {
    try {
      // For creating, we expect MatterFormData; for updating we'd handle UpdateMatterRequest
      const matterData = data as MatterFormData;
      
      // Validate that we have a clientId
      if (!matterData.clientId) {
        console.error('Cannot create matter: No client selected');
        return;
      }
      
      // TODO: Implement create functionality with enhanced hook
      console.log('Create matter:', matterData);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create matter:', error);
    }
  };

  if (error) {
    return (
      <div className="space-y-8">
        <div className="bg-red-900 border border-red-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-300 mb-2">Error Loading Matters</h3>
          <p className="text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Matter Management</h1>
          <p className="text-gray-400 mt-1">Manage and track all your legal matters</p>
        </div>
        
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>New Matter</span>
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search matters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Client Filter */}
          <div className="flex items-center space-x-2">
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Clients</option>
              {uniqueClients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Matter List */}
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="bg-gray-800 border border-gray-700 rounded-lg p-6 animate-pulse"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="h-5 bg-gray-700 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-700 rounded w-1/3"></div>
                </div>
                <div className="h-6 w-20 bg-gray-700 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredMatters.length === 0 ? (
        <div className="text-center py-12">
          <Briefcase className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">
            {searchTerm || statusFilter !== 'all' ? 'No matching matters' : 'No matters found'}
          </h3>
          <p className="text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Create your first matter to get started.'
            }
          </p>
        </div>
      ) : (
        <EnhancedMatterList
          matters={filteredMatters}
          isLoading={false}
          onMatterClick={handleMatterClick}
          onEditMatter={handleEditMatter}
          onDeleteMatter={handleDeleteMatter}
          onClientClick={handleClientClick}
          showClientColumn={true}
        />
      )}

      {/* Create Matter Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-white mb-4">Create New Matter</h2>
              <MatterForm
                onSubmit={handleCreateMatter}
                onCancel={() => setShowCreateForm(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Matters;
